import { useState } from "react";
import { useJournalShare } from "../../hooks/useJournalShare";

const ShareJournalModal = ({ isOpen, onClose, childId, childName }) => {
  const {
    viewerEmails, publicJournalEnabled, publicJournalId,
    loading, saving,
    addViewer, removeViewer,
    enablePublicLink, disablePublicLink, regeneratePublicLink,
  } = useJournalShare(childId);

  const [tab,         setTab]      = useState("email");
  const [newEmail,    setNewEmail] = useState("");
  const [emailError,  setEmailError] = useState("");
  const [copied,      setCopied]   = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  if (!isOpen) return null;

  const publicUrl = publicJournalId
    ? `${window.location.origin}/journal/${publicJournalId}`
    : null;

  const handleAddEmail = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (viewerEmails.includes(email)) {
      setEmailError("This email already has access.");
      return;
    }
    setEmailError("");
    await addViewer(email);
    setNewEmail("");
  };

  const handleCopy = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleTogglePublic = async () => {
    if (publicJournalEnabled) {
      await disablePublicLink();
    } else {
      await enablePublicLink(childName);
    }
  };

  const handleRegen = async () => {
    if (!confirmRegen) { setConfirmRegen(true); return; }
    await regeneratePublicLink(childName);
    setConfirmRegen(false);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1050,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--bg-white)", borderRadius: "14px",
        width: "100%", maxWidth: "480px", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      }}>
        {/* Header */}
        <div style={{
          padding: "1.1rem 1.25rem",
          borderBottom: "1px solid var(--border-light)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700 }}>Share Journal</h6>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-light)" }}>
              {childName}'s baby book
            </p>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} style={{ lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex", borderBottom: "1px solid var(--border-light)",
          padding: "0 1.25rem",
        }}>
          {[
            { id: "email",  label: "✉ Email Access" },
            { id: "public", label: "🔗 Public Link"  },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "0.75rem 1rem 0.65rem",
                fontSize: "0.85rem", fontWeight: 600,
                color: tab === t.id ? "var(--primary-dark)" : "var(--text-light)",
                borderBottom: tab === t.id ? "2px solid var(--primary-dark)" : "2px solid transparent",
                marginBottom: "-1px",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
          {loading ? (
            <p style={{ color: "var(--text-light)", textAlign: "center" }}>Loading…</p>
          ) : tab === "email" ? (
            <>
              <p style={{ fontSize: "0.83rem", color: "var(--text-light)", marginBottom: "1rem" }}>
                Add someone's email address below. Once they create an account (or sign in) with
                that email, they'll have <strong>read-only access</strong> to {childName}'s journal.
              </p>

              {/* Add email form */}
              <form onSubmit={handleAddEmail} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  placeholder="grandma@email.com"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                  style={{ flex: 1 }}
                />
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={saving || !newEmail.trim()}
                >
                  Add
                </button>
              </form>
              {emailError && (
                <p style={{ fontSize: "0.78rem", color: "#ef5350", marginBottom: "0.75rem" }}>{emailError}</p>
              )}

              {/* Viewer list */}
              {viewerEmails.length === 0 ? (
                <p style={{ fontSize: "0.83rem", color: "var(--text-light)", marginTop: "0.75rem" }}>
                  No viewers added yet.
                </p>
              ) : (
                <div style={{ marginTop: "0.75rem" }}>
                  <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-light)", marginBottom: "0.4rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Current viewers ({viewerEmails.length})
                  </p>
                  {viewerEmails.map((email) => (
                    <div key={email} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "0.5rem 0.75rem", marginBottom: "0.35rem",
                      background: "var(--bg-light, #fafafa)",
                      borderRadius: "8px", border: "1px solid var(--border-light)",
                    }}>
                      <span style={{ fontSize: "0.88rem" }}>{email}</span>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeViewer(email)}
                        disabled={saving}
                        style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem" }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p style={{ fontSize: "0.83rem", color: "var(--text-light)", marginBottom: "1rem" }}>
                A public link lets <strong>anyone with the link</strong> view {childName}'s journal
                without needing an account. The link is a random unguessable URL — disable or regenerate
                it at any time to revoke access for everyone.
              </p>

              {/* Toggle */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "0.75rem 1rem", borderRadius: "10px",
                border: "1px solid var(--border-light)",
                marginBottom: "1rem",
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: "0.9rem" }}>
                    {publicJournalEnabled ? "Public link is active" : "Public link is off"}
                  </p>
                  <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-light)" }}>
                    {publicJournalEnabled ? "Anyone with the link can view the journal" : "Enable to generate a shareable link"}
                  </p>
                </div>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={publicJournalEnabled}
                    onChange={handleTogglePublic}
                    disabled={saving}
                    style={{ cursor: "pointer", width: "2.5rem", height: "1.25rem" }}
                  />
                </div>
              </div>

              {/* Link display */}
              {publicJournalEnabled && publicUrl && (
                <>
                  <div style={{
                    background: "var(--bg-light, #fafafa)",
                    borderRadius: "8px", padding: "0.65rem 0.85rem",
                    border: "1px solid var(--border-light)",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    marginBottom: "0.75rem",
                  }}>
                    <span style={{
                      flex: 1, fontSize: "0.78rem", color: "var(--text-dark)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {publicUrl}
                    </span>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleCopy}
                      style={{ flexShrink: 0, fontSize: "0.78rem" }}
                    >
                      {copied ? "Copied ✓" : "Copy"}
                    </button>
                  </div>

                  {/* Regenerate */}
                  {confirmRegen ? (
                    <div style={{
                      background: "#FFEBEE", borderRadius: "8px",
                      padding: "0.75rem 1rem", border: "1px solid #EF9A9A",
                    }}>
                      <p style={{ margin: "0 0 0.5rem", fontSize: "0.83rem", color: "#C62828", fontWeight: 600 }}>
                        This will break the existing link immediately.
                      </p>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button className="btn btn-sm btn-danger" onClick={handleRegen} disabled={saving}>
                          {saving ? "Regenerating…" : "Yes, regenerate"}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmRegen(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setConfirmRegen(true)}
                      style={{ fontSize: "0.8rem" }}
                    >
                      🔄 Regenerate link
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "0.9rem 1.25rem", borderTop: "1px solid var(--border-light)", textAlign: "right" }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ShareJournalModal;
